import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, Loader2, Pencil, PlugZap, Plus, Power, Star, XCircle } from 'lucide-react'
import { bcApi } from '../../services/api'
import { useToast } from '../../context/ToastContext'
import {
  CollapsibleSection, ConfirmDialog, Modal, PanelState, cardCls, errorMessage, inputCls, primaryBtn, secondaryBtn,
} from '../opco/admin/shared'

const EMPTY = {
  connection_label: '', tenant_id: '', client_id: '', client_secret: '',
  bc_environment_name: '', company_name: '', is_default: false, shared: false,
}

// A connection is one Business Central tenant, environment and company that templates
// post to. Managed by administrators; the client secret is write-only (the server never
// sends it back), so editing leaves it blank to keep the saved one.
//
// `opco` given: the connections of that OpCo (and the shared ones), for the OpCos admin
// page, shown as a row that starts collapsed (credentials are rarely touched; the header
// summarises the state). No `opco`: every connection of the organisation as a plain card,
// for a deployment without OpCo workspaces.
export default function BcConnectionsPanel({ opco = null }) {
  const toast = useToast()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(null) // null | 'new' | connection row
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [busyId, setBusyId] = useState(null)
  const [results, setResults] = useState({}) // connection id -> { ok, message }
  const [deactivating, setDeactivating] = useState(null)
  const [open, setOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await bcApi.getConnections()
      const all = res.data?.connections || []
      setRows(opco ? all.filter(c => !c.opco_code || c.opco_code === opco.code) : all)
    } catch (err) {
      setError(errorMessage(err, 'Could not load the Business Central connections'))
    } finally {
      setLoading(false)
    }
  }, [opco])

  useEffect(() => { load() }, [load])

  const openNew = () => {
    setForm({ ...EMPTY, is_default: rows.length === 0 })
    setFormError('')
    setEditing('new')
  }

  const openEdit = (row) => {
    setForm({
      ...EMPTY,
      connection_label: row.connection_label || '',
      tenant_id: row.tenant_id || '',
      client_id: row.client_id || '',
      bc_environment_name: row.bc_environment_name || '',
      company_name: row.company_name || '',
      is_default: Boolean(row.is_default),
      shared: Boolean(opco) && !row.opco_code,
    })
    setFormError('')
    setEditing(row)
  }

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }))

  const isNew = editing === 'new'
  const missing = [
    !form.connection_label.trim() && 'name',
    isNew && !form.tenant_id.trim() && 'tenant ID',
    isNew && !form.client_id.trim() && 'client ID',
    isNew && !form.client_secret.trim() && 'client secret',
    !form.bc_environment_name.trim() && 'environment',
    !form.company_name.trim() && 'company',
  ].filter(Boolean)

  const save = async (e) => {
    e.preventDefault()
    if (missing.length) return
    setSaving(true)
    setFormError('')
    try {
      const body = {
        connection_label: form.connection_label.trim(),
        bc_environment_name: form.bc_environment_name.trim(),
        company_name: form.company_name.trim(),
        is_default: form.is_default,
      }
      if (isNew) {
        body.tenant_id = form.tenant_id.trim()
        body.client_id = form.client_id.trim()
      }
      // Blank leaves the saved secret alone: the server rejects a blank on update.
      if (form.client_secret.trim()) body.client_secret = form.client_secret.trim()
      if (opco) body.opco_code = form.shared ? '' : opco.code

      if (isNew) await bcApi.createConnection(body)
      else await bcApi.updateConnection(editing.bc_connection_id, body)
      toast.success(isNew ? 'Connection added' : 'Connection saved', `${body.connection_label} · use Test to check it`)
      setEditing(null)
      await load()
    } catch (err) {
      setFormError(errorMessage(err, 'Could not save the connection'))
    } finally {
      setSaving(false)
    }
  }

  const test = async (row) => {
    setBusyId(row.bc_connection_id)
    try {
      const res = await bcApi.testConnectionById(row.bc_connection_id)
      const d = res.data || {}
      setResults(prev => ({
        ...prev,
        [row.bc_connection_id]: {
          ok: Boolean(d.success),
          message: d.success ? `Connected to ${d.company?.name || row.company_name}` : (d.message || 'The connection test failed.'),
        },
      }))
    } catch (err) {
      setResults(prev => ({ ...prev, [row.bc_connection_id]: { ok: false, message: errorMessage(err, 'The connection test failed.') } }))
    } finally {
      setBusyId(null)
    }
  }

  const makeDefault = async (row) => {
    setBusyId(row.bc_connection_id)
    try {
      await bcApi.updateConnection(row.bc_connection_id, { is_default: true })
      toast.success('Default connection changed', row.connection_label)
      await load()
    } catch (err) {
      toast.error('Could not change the default', errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  const setActive = async (row, active) => {
    setBusyId(row.bc_connection_id)
    try {
      if (active) await bcApi.updateConnection(row.bc_connection_id, { is_active: true })
      else await bcApi.deactivateConnection(row.bc_connection_id)
      toast.success(active ? 'Connection reactivated' : 'Connection deactivated', row.connection_label)
      setDeactivating(null)
      await load()
    } catch (err) {
      toast.error(active ? 'Could not reactivate' : 'Could not deactivate', errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  const body = (
    <>
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 dark:border-gray-800 px-5 py-4">
        <div>
          {!opco && (
            <div className="flex items-center gap-2">
              <PlugZap className="h-4 w-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Business Central connections</h2>
            </div>
          )}
          <p className={`${opco ? '' : 'mt-1 '}text-xs text-gray-500 dark:text-gray-400`}>
            The Business Central environments and companies templates post to.
            {opco ? ` Connections of ${opco.name}, plus any shared with every OpCo.` : ''}
          </p>
        </div>
        <button type="button" onClick={openNew} className={primaryBtn}>
          <Plus className="h-4 w-4" /> Add connection
        </button>
      </div>

      <PanelState
        loading={loading}
        error={error}
        onRetry={load}
        empty={rows.length === 0}
        emptyText="No connections yet. Add one so templates can post to Business Central."
      >
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map(row => {
            const result = results[row.bc_connection_id]
            const busy = busyId === row.bc_connection_id
            return (
              <li key={row.bc_connection_id} className="px-5 py-3.5">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{row.connection_label}</p>
                  {row.is_default && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-navy-50 px-2 py-0.5 text-[11px] font-semibold text-navy-700 dark:bg-navy-900/30 dark:text-navy-300">
                      <Star className="h-3 w-3" /> Default
                    </span>
                  )}
                  {row.is_active === false && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">Inactive</span>
                  )}
                  {opco && !row.opco_code && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">Shared by every OpCo</span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {row.bc_environment_name} · {row.company_name}
                </p>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => test(row)} disabled={busy || row.is_active === false} className={secondaryBtn}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Test
                  </button>
                  <button type="button" onClick={() => openEdit(row)} disabled={busy} className={secondaryBtn}>
                    <Pencil className="h-4 w-4" /> Edit
                  </button>
                  {!row.is_default && row.is_active !== false && (
                    <button type="button" onClick={() => makeDefault(row)} disabled={busy} className={secondaryBtn}>
                      <Star className="h-4 w-4" /> Make default
                    </button>
                  )}
                  {row.is_active === false ? (
                    <button type="button" onClick={() => setActive(row, true)} disabled={busy} className={secondaryBtn}>
                      <Power className="h-4 w-4" /> Reactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDeactivating(row)}
                      disabled={busy}
                      className={`${secondaryBtn} text-red-600 dark:text-red-400`}
                    >
                      <Power className="h-4 w-4" /> Deactivate
                    </button>
                  )}
                </div>

                {result && (
                  <p
                    role="status"
                    className={`mt-2 flex items-center gap-1.5 text-xs ${result.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                  >
                    {result.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    {result.message}
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      </PanelState>

      {editing && (
        <Modal
          title={isNew ? 'Add a Business Central connection' : 'Edit connection'}
          onClose={saving ? undefined : () => setEditing(null)}
          labelledBy="bc-connection-title"
          footer={(
            <>
              <button type="button" onClick={() => setEditing(null)} disabled={saving} className={secondaryBtn}>Cancel</button>
              <button type="submit" form="bc-connection-form" disabled={saving || missing.length > 0} className={primaryBtn}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {isNew ? 'Add connection' : 'Save'}
              </button>
            </>
          )}
        >
          <form id="bc-connection-form" onSubmit={save} className="space-y-3" noValidate>
            {formError && (
              <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/10 dark:text-red-300">
                {formError}
              </p>
            )}
            <Labeled id="bcc-label" label="Name">
              <input id="bcc-label" value={form.connection_label} onChange={set('connection_label')} className={inputCls} placeholder="e.g. UK company, Production" autoComplete="off" />
            </Labeled>
            {isNew && (
              <div className="grid gap-3 sm:grid-cols-2">
                <Labeled id="bcc-tenant" label="Tenant ID">
                  <input id="bcc-tenant" value={form.tenant_id} onChange={set('tenant_id')} className={inputCls} autoComplete="off" />
                </Labeled>
                <Labeled id="bcc-client" label="Client ID">
                  <input id="bcc-client" value={form.client_id} onChange={set('client_id')} className={inputCls} autoComplete="off" />
                </Labeled>
              </div>
            )}
            <Labeled id="bcc-secret" label="Client secret">
              <input
                id="bcc-secret"
                type="password"
                value={form.client_secret}
                onChange={set('client_secret')}
                className={inputCls}
                placeholder={isNew ? '' : 'Saved — leave blank to keep'}
                autoComplete="new-password"
              />
            </Labeled>
            <div className="grid gap-3 sm:grid-cols-2">
              <Labeled id="bcc-env" label="Environment">
                <input id="bcc-env" value={form.bc_environment_name} onChange={set('bc_environment_name')} className={inputCls} placeholder="Production" autoComplete="off" />
              </Labeled>
              <Labeled id="bcc-company" label="Company">
                <input id="bcc-company" value={form.company_name} onChange={set('company_name')} className={inputCls} placeholder="CRONUS UK Ltd." autoComplete="off" />
              </Labeled>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={e => setForm(prev => ({ ...prev, is_default: e.target.checked }))}
              />
              Use as the default connection{opco ? ` for ${opco.name}` : ''}
            </label>
            {opco && (
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={form.shared}
                  onChange={e => setForm(prev => ({ ...prev, shared: e.target.checked }))}
                />
                Share with every OpCo
              </label>
            )}
            {isNew && missing.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">Still needed: {missing.join(', ')}.</p>
            )}
          </form>
        </Modal>
      )}

      {deactivating && (
        <ConfirmDialog
          title="Deactivate connection"
          message={`Templates bound to ${deactivating.connection_label} will not be able to post until it is reactivated or they are bound to another connection.`}
          confirmLabel="Deactivate"
          danger
          busy={busyId === deactivating.bc_connection_id}
          onConfirm={() => setActive(deactivating, false)}
          onCancel={() => setDeactivating(null)}
        />
      )}
    </>
  )

  if (!opco) {
    return <section className={cardCls} aria-label="Business Central connections">{body}</section>
  }

  // The collapsed header says where this OpCo stands without opening it.
  const live = rows.filter(r => r.is_active !== false)
  const shown = live.find(r => r.is_default) || live[0]
  const shownResult = shown ? results[shown.bc_connection_id] : null
  let summary = 'Not set up'
  let tone = 'muted'
  if (loading) summary = 'Loading...'
  else if (error) { summary = 'Could not load'; tone = 'bad' }
  else if (shown) {
    summary = shown.connection_label + (live.length > 1 ? ` +${live.length - 1} more` : '')
    if (shownResult) {
      summary += ` · ${shownResult.ok ? 'Connected' : 'Last test failed'}`
      tone = shownResult.ok ? 'ok' : 'bad'
    }
  }

  return (
    <CollapsibleSection
      id={`bc-body-${opco.id}`}
      icon={PlugZap}
      title="Business Central"
      ariaLabel="Business Central connections"
      summary={summary}
      tone={tone}
      open={open}
      onToggle={() => setOpen(v => !v)}
    >
      {body}
    </CollapsibleSection>
  )
}

function Labeled({ id, label, children }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">{label}</label>
      {children}
    </div>
  )
}
